import {
  AsyncPipe,
  DatePipe,
  DecimalPipe,
  KeyValuePipe,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { PricingPartnerService } from '@app/modules/pricing/services/pricing-partner.service';
import { PricingServiceDataService } from '@app/modules/pricing/services/pricing-service-data.service';
import {
  Discount,
  DiscountPriceType,
  DiscountType,
  PricingServiceData,
  ValueType,
} from '@app/modules/pricing/types/pricing.types';
import { ServiceDataUtilsService } from '@app/modules/service-data/services/service-data-utils.service';
import {
  GetServiceDataAction,
  SetGeneplanetInvoiceAction,
  SetPaymentTypeAction,
} from '@app/modules/service-data/store/service-data.actions';
import { ServiceDataState } from '@app/modules/service-data/store/service-data.state';
import {
  ExtendedServiceData,
  PaymentService,
} from '@app/modules/service-data/types/service-data.types';
import { Bundle, InvoicePayment, PaymentType } from '@core/core.types';
import {
  AddInvoicePaymentsAction,
  MarkAsPaidAction,
  MarkInvoicePaymentAsPaidAction,
  RemoveInvoicePaymentAction,
  RevokeInvoicePaid,
  SetDiscountAction,
  SetLaboratoryPriceAction,
  UpdateInvoiceAction,
} from '@core/store/payments/invoice-payments.actions';
import { ProductsListState } from '@core/store/products/products.state';
import { Util } from '@core/utils/core.util';
import {
  NgbActiveModal,
  NgbDatepickerModule,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableDataType,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import { GetListPipe } from '@shared/pipes/get-list.pipe';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { first } from 'rxjs/internal/operators/first';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { CurrenciesTypeaheadComponent } from './currencies-typeahead/currencies-typeahead.component';

interface ServiceDataPrices {
  endUserPrice: number | null;
  doctorFee: number | null;
  currency: string | null;
  eupDiscounts: Discount[];
}

@Component({
  selector: 'app-service-data-finance-details',
  providers: [DatePipe, DecimalPipe],
  imports: [
    AsyncPipe,
    DatePipe,
    DecimalPipe,
    GetListPipe,
    TranslateModule,
    KeyValuePipe,
    FormsModule,
    ReactiveFormsModule,
    DataTableComponent,
    RequirePermissionDirective,
    CurrenciesTypeaheadComponent,
    NgbDatepickerModule,
    SelectComponent,
    SelectOptionComponent,
  ],
  templateUrl: './service-data-finance-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./service-data-finance-details.component.scss'],
})
export class ServiceDataFinanceDetailsComponent implements OnInit {
  private store = inject(Store);
  protected serviceDataUtils = inject(ServiceDataUtilsService);
  protected modalRef = inject(NgbActiveModal);
  private pricingServiceDataService = inject(PricingServiceDataService);
  private pricingPartnerService = inject(PricingPartnerService);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);
  private datePipe = inject(DatePipe);
  private decimalPipe = inject(DecimalPipe);

  @Input() id: string;

  private serviceDataBundles: Bundle[] = [];

  @Select(ProductsListState.getBundlesList) bundles$: Observable<Bundle[]>;

  serviceData$: Observable<ExtendedServiceData>;
  invoiceId: string;

  prices$ = new BehaviorSubject<ServiceDataPrices>({
    endUserPrice: null,
    doctorFee: null,
    currency: null,
    eupDiscounts: [],
  });

  sumPrice$ = new BehaviorSubject<number>(0);

  eupDiscountsDataTableConfiguration: DataTableConfiguration;

  gpInvoiceField = new FormControl('');
  laboratoryPriceField = new FormControl<number>(null);
  discountField = new FormControl<number>(null);
  sepaField = new FormControl<boolean>(false);
  paymentTypeField = new FormControl<PaymentType>(null);

  paidInFullEnabled$: Observable<boolean>;

  paymentFormGroup = this.fb.group({
    paymentDate: [moment(), [Validators.required]],
    price: [0, [Validators.required, Validators.min(1)]],
    currency: ['', [Validators.required]],
    service: [null],
    notes: ['', Validators.required],
  });

  paymentsDataTableConfiguration: DataTableConfiguration;

  protected paymentActionInProgress = signal(false);

  ngOnInit() {
    this.serviceData$ = this.store
      .dispatch(new GetServiceDataAction(this.id, false, false))
      .pipe(
        switchMap(() =>
          this.store
            .select(ServiceDataState.getServiceDataById)
            .pipe(map((getById) => getById(this.id)))
        ),
        switchMap((serviceData) =>
          this.serviceDataUtils.getExtendedServiceData(serviceData, {
            partner: true,
            bundles: true,
            invoice: true,
            samples: true,
          })
        ),
        takeUntilDestroyed(this.destroyRef),
        tap((serviceData) => {
          this.invoiceId = serviceData.invoice.id;
          this.gpInvoiceField.setValue(
            serviceData.paymentInformation.geneplanetInvoiceNumber
          );
          this.laboratoryPriceField.setValue(
            serviceData.invoice.laboratoryPrice
          );
          this.discountField.setValue(serviceData.invoice.discountValue);
          this.paymentTypeField.setValue(
            serviceData.paymentInformation.paymentType
          );
          this.sepaField.setValue(
            serviceData.invoice?.properties?.find(
              (property) => property.itemKey === 'SepaContract'
            )?.itemValue === 'true'
          );
          this.paymentFormGroup
            .get('notes')
            .setValue(
              `${serviceData.paymentInformation.notes} for ${serviceData.patientInformation.firstName} ${serviceData.patientInformation.lastName}`
            );

          if (serviceData.invoice.paid) {
            this.sepaField.disable();
          } else {
            this.sepaField.enable();
          }
        }),
        tap((serviceData) => this.loadPrices(serviceData)),
        shareReplay(1)
      );

    this.eupDiscountsDataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [],
      columns: [
        {
          label: 'serviceData.financeDetails.eupDiscounts.columns.type',
          invoke: {
            objectReference: {
              getType: (discount: Discount) =>
                this.translateService.instant(
                  'pricing.discount.list.discountType.' + discount.discountType
                ),
            },
            name: 'getType',
            args: [],
          },
        },
        {
          label: 'serviceData.financeDetails.eupDiscounts.columns.amount',
          invoke: {
            objectReference: {
              getAmount: (discount: Discount) => this.formatDiscount(discount),
            },
            name: 'getAmount',
            args: [],
          },
        },
        {
          label: 'serviceData.financeDetails.eupDiscounts.columns.bundle',
          invoke: {
            objectReference: {
              getBundle: (discount: Discount) =>
                discount.discountType === DiscountType.Individual
                  ? discount.serviceDataReferenceKey
                  : this.getDiscountBundleNames(discount),
            },
            name: 'getBundle',
            args: [],
          },
        },
        {
          label: 'serviceData.financeDetails.eupDiscounts.columns.validity',
          invoke: {
            objectReference: {
              getValidity: (discount: Discount) =>
                discount.discountType === DiscountType.General
                  ? `${this.datePipe.transform(discount.validFrom, 'dd.MM.yyyy')} - ${this.datePipe.transform(discount.validTo, 'dd.MM.yyyy')}`
                  : '—',
            },
            name: 'getValidity',
            args: [],
          },
        },
      ],
      rows$: this.prices$.pipe(map((prices) => prices.eupDiscounts)),
    };

    this.paymentsDataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          name: 'serviceData.financeDetails.actions.markAsPaid',
          icon: 'fa-light fa-circle-check',
          invisible: (payment) => !!payment.paid,
          disabled: () => this.paymentActionInProgress(),
          type: DataTableActionType.BUTTON,
          action: (event, payment) =>
            this.dispatchPaymentAction(
              new MarkInvoicePaymentAsPaidAction(this.invoiceId, payment.id)
            ),
        },
        {
          name: 'serviceData.financeDetails.actions.paid',
          icon: 'fa-light fa-check-double',
          disabled: () => true,
          invisible: (payment) => !payment.paid,
          type: DataTableActionType.BUTTON,
        },
        {
          name: 'serviceData.financeDetails.actions.removePayment',
          icon: 'fa-light fa-minus',
          disabled: () => this.paymentActionInProgress(),
          type: DataTableActionType.BUTTON,
          action: (event, payment) =>
            this.dispatchPaymentAction(
              new RemoveInvoicePaymentAction(this.invoiceId, payment.id)
            ),
        },
      ],
      columns: [
        {
          label: '#',
          customStyle: 'width: 1%;',
          property: 'index',
        },
        {
          label: 'serviceData.financeDetails.columns.date',
          property: 'paymentDate',
          type: DataTableDataType.DATE,
        },
        {
          label: 'serviceData.financeDetails.columns.price',
          property: 'price',
        },
        {
          label: 'serviceData.financeDetails.columns.currency',
          property: 'currency',
        },
        {
          label: 'serviceData.financeDetails.columns.paymentService',
          invoke: {
            objectReference: {
              getPaymentService: (payment: InvoicePayment) =>
                payment.service
                  ? this.translateService.instant(
                      'serviceData.financeDetails.paymentServices.' +
                        payment.service
                    )
                  : '—',
            },
            name: 'getPaymentService',
            args: [],
          },
        },
        {
          label: 'serviceData.financeDetails.columns.payerNameOrNote',
          property: 'notes',
        },
      ],
      rows$: this.serviceData$.pipe(
        map((serviceData) =>
          serviceData.invoice.payments.map((payment, index) => ({
            ...payment,
            index: index + 1,
          }))
        ),
        tap((payments: InvoicePayment[]) => {
          this.sumPrice$.next(
            payments.reduce((prev, current) => prev + current.price, 0) +
              (this.discountField.value || 0)
          );
        })
      ),
    };

    this.paidInFullEnabled$ = combineLatest([
      this.sumPrice$,
      this.prices$,
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(([sumPrice, prices]) => {
        if (sumPrice > 0 && sumPrice >= (prices.endUserPrice ?? 0)) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  private loadPrices(serviceData: ExtendedServiceData) {
    this.serviceDataBundles = serviceData.bundles ?? [];

    forkJoin({
      pricing: this.pricingServiceDataService
        .getByReferenceKey(serviceData.referenceKey)
        .pipe(catchError(() => of(null as PricingServiceData | null))),
      currency: this.pricingPartnerService
        .getPricingPartner(serviceData.partnerId)
        .pipe(
          map((pricingPartner) => pricingPartner?.currency ?? null),
          catchError(() => of(null as string | null))
        ),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ pricing, currency }) =>
        this.prices$.next(this.toServiceDataPrices(pricing, currency))
      );
  }

  private toServiceDataPrices(
    pricing: PricingServiceData | null,
    currency: string | null
  ): ServiceDataPrices {
    if (!pricing) {
      return {
        endUserPrice: null,
        doctorFee: null,
        currency,
        eupDiscounts: [],
      };
    }

    const doctorFee =
      pricing.eupPrice != null && pricing.wholesalePrice != null
        ? pricing.eupPrice - pricing.wholesalePrice
        : null;

    return {
      endUserPrice: pricing.discountedEupPrice ?? pricing.eupPrice,
      doctorFee,
      currency,
      eupDiscounts: pricing.discounts.filter(
        (discount) => discount.priceType === DiscountPriceType.EUP
      ),
    };
  }

  private formatDiscount(discount: Discount): string {
    const currency = this.prices$.value.currency ?? '';
    const value =
      discount.valueType === ValueType.Percentage
        ? `${discount.discountValue} %`
        : `${this.decimalPipe.transform(discount.discountValue, '1.2-2')} ${currency}`;

    return discount.pivot?.amount != null
      ? `${value} (−${this.decimalPipe.transform(discount.pivot.amount, '1.2-2')} ${currency})`
      : value;
  }

  private getDiscountBundleNames(discount: Discount): string {
    const names = this.serviceDataBundles
      .filter((bundle) => discount.bundleIds?.includes(bundle.id))
      .map((bundle) => bundle.name);

    return names.length ? names.join(', ') : '—';
  }

  saveLaboratoryPrice() {
    if (!this.laboratoryPriceField.dirty) {
      return;
    }
    this.serviceData$
      .pipe(
        first(),
        switchMap((serviceData) =>
          this.store.dispatch(
            new SetLaboratoryPriceAction(
              serviceData.invoice.id,
              this.laboratoryPriceField.value
            )
          )
        )
      )
      .subscribe(() => this.laboratoryPriceField.markAsPristine());
  }

  savePaymentType() {
    if (!this.paymentTypeField.dirty) {
      return;
    }
    this.serviceData$
      .pipe(
        first(),
        switchMap((serviceData) =>
          this.store.dispatch(
            new SetPaymentTypeAction(
              serviceData.id,
              this.paymentTypeField.value
            )
          )
        )
      )
      .subscribe(() => this.paymentTypeField.markAsPristine());
  }

  saveDiscount() {
    if (!this.discountField.dirty) {
      return;
    }
    this.serviceData$
      .pipe(
        first(),
        switchMap((serviceData) =>
          this.store.dispatch(
            new SetDiscountAction(
              serviceData.invoice.id,
              this.discountField.value
            )
          )
        )
      )
      .subscribe(() => this.discountField.markAsPristine());
  }

  saveGpInvoice() {
    if (!this.gpInvoiceField.dirty) {
      return;
    }
    this.serviceData$
      .pipe(
        first(),
        switchMap((serviceData) =>
          this.store.dispatch(
            new SetGeneplanetInvoiceAction(
              serviceData.id,
              this.gpInvoiceField.value
            )
          )
        )
      )
      .subscribe(() => this.gpInvoiceField.markAsPristine());
  }

  addPayment(invoiceId: string) {
    this.store.dispatch(
      new AddInvoicePaymentsAction(invoiceId, [
        {
          ...(this.paymentFormGroup.getRawValue() as any),
          id: Util.CreateGuid(),
        },
      ])
    );
  }

  toggleSepa(invoiceId, sepa) {
    this.store.dispatch(new UpdateInvoiceAction(invoiceId, sepa));
  }

  markAsPaidInFull() {
    this.dispatchPaymentAction(new MarkAsPaidAction(this.invoiceId));
  }

  revokePaidInFull() {
    this.dispatchPaymentAction(new RevokeInvoicePaid(this.invoiceId));
  }

  private dispatchPaymentAction(action: object) {
    this.paymentActionInProgress.set(true);
    this.store
      .dispatch(action)
      .pipe(finalize(() => this.paymentActionInProgress.set(false)))
      .subscribe();
  }

  protected readonly PaymentType = PaymentType;
  protected readonly PaymentService = PaymentService;
  protected readonly Object = Object;
}
